import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Machine, Sensor } from './MachineContext';
import { telemetryApi } from '../services/apiClient';

// ── Types ──────────────────────────────────────────────────────

export interface TelemetryPoint {
  ts:    number;   // unix ms
  time:  string;   // display label
  value: number;
}

export interface SensorLive {
  sensorId:    string;
  machineId:   string;
  currentValue: number;
  history:     TelemetryPoint[];  // sliding window, last 30 points
  status:      'ok' | 'warning' | 'critical';
  lastUpdated: number;
}

export interface MachineHeartbeat {
  machineId:   string;
  lastSeen:    number;   // unix ms
  status:      'active' | 'idle' | 'offline';
}

export interface ActiveAlert {
  id:          string;
  machineId:   string;
  machineName: string;
  sensorId:    string;
  sensorName:  string;
  value:       number;
  threshold:   number;
  direction:   'high' | 'low';
  severity:    'WARNING' | 'CRITICAL';
  ts:          number;
  acked:       boolean;
}

interface TelemetryCtx {
  sensorLive:    Record<string, SensorLive>;   // key = sensorId
  heartbeats:    Record<string, MachineHeartbeat>; // key = machineId
  activeAlerts:  ActiveAlert[];
  ackAlert:      (id: string) => void;
  getSensorHistory: (sensorId: string) => TelemetryPoint[];
  isLive:        boolean;
}

const TelemetryContext = createContext<TelemetryCtx>({
  sensorLive: {}, heartbeats: {}, activeAlerts: [],
  ackAlert: () => {}, getSensorHistory: () => [], isLive: false,
});

// ── Simulation helpers ─────────────────────────────────────────

const HISTORY_SIZE = 30;
const TICK_MS      = 2000; // simulate every 2s

// Realistic sensor simulation with drift + noise
function nextValue(sensor: Sensor, prev: number): number {
  const mid   = (sensor.minThreshold + sensor.maxThreshold) / 2;
  const range = (sensor.maxThreshold - sensor.minThreshold);
  // Drift toward midpoint + random walk + occasional spike
  const drift = (mid - prev) * 0.05;
  const noise = (Math.random() - 0.5) * range * 0.08;
  const spike = Math.random() > 0.97 ? (Math.random() - 0.5) * range * 0.4 : 0;
  return Math.max(
    sensor.minThreshold * 0.8,
    Math.min(sensor.maxThreshold * 1.15, +(prev + drift + noise + spike).toFixed(2))
  );
}

function evalStatus(value: number, sensor: Sensor): SensorLive['status'] {
  const range = sensor.maxThreshold - sensor.minThreshold;
  const warnBand = range * 0.1; // 10% of range = warning zone
  if (value > sensor.maxThreshold + warnBand || value < sensor.minThreshold - warnBand) return 'critical';
  if (value > sensor.maxThreshold || value < sensor.minThreshold) return 'warning';
  return 'ok';
}

// ── Provider ───────────────────────────────────────────────────

interface Props {
  machines: Machine[];
  children: React.ReactNode;
}

export const TelemetryProvider: React.FC<Props> = ({ machines, children }) => {
  const [sensorLive,   setSensorLive]   = useState<Record<string, SensorLive>>({});
  const [heartbeats,   setHeartbeats]   = useState<Record<string, MachineHeartbeat>>({});
  const [activeAlerts, setActiveAlerts] = useState<ActiveAlert[]>([]);
  const stateRef = useRef<Record<string, SensorLive>>({});

  // Initialise state for all sensors when machines change
  useEffect(() => {
    const initial: Record<string, SensorLive> = {};
    const hb: Record<string, MachineHeartbeat> = {};

    machines.forEach(m => {
      hb[m.machineId] = {
        machineId: m.machineId,
        lastSeen:  Date.now(),
        status:    m.status === 'offline' ? 'offline' : m.status === 'maintenance' ? 'idle' : 'active',
      };
      m.sensors.forEach(s => {
        const seed = s.currentValue ?? (s.minThreshold + s.maxThreshold) / 2;
        const history: TelemetryPoint[] = Array.from({ length: HISTORY_SIZE }, (_, i) => {
          const t = Date.now() - (HISTORY_SIZE - i) * TICK_MS;
          return { ts: t, time: new Date(t).toLocaleTimeString(), value: seed };
        });
        initial[s.sensorId] = {
          sensorId: s.sensorId, machineId: m.machineId,
          currentValue: seed, history, status: evalStatus(seed, s),
          lastUpdated: Date.now(),
        };
      });
    });

    stateRef.current = initial;
    setSensorLive(initial);
    setHeartbeats(hb);
  }, [machines]);

  // Tick — advance simulation for all active machine sensors
  useEffect(() => {
    const iv = setInterval(() => {
      const now  = Date.now();
      const next = { ...stateRef.current };
      const newAlerts: ActiveAlert[] = [];

      machines.forEach(m => {
        // Skip offline/maintenance machines — they go silent
        if (m.status === 'offline') return;
        if (m.status === 'maintenance' && Math.random() > 0.1) return;

        m.sensors.forEach(s => {
          const prev = next[s.sensorId];
          if (!prev) return;

          const value  = nextValue(s, prev.currentValue);
          const status = evalStatus(value, s);
          const point: TelemetryPoint = {
            ts: now, time: new Date(now).toLocaleTimeString(), value,
          };

          next[s.sensorId] = {
            ...prev,
            currentValue: value,
            history:      [...prev.history.slice(-(HISTORY_SIZE - 1)), point],
            status,
            lastUpdated:  now,
          };

          // Threshold breach → generate alert
          if (status !== 'ok') {
            const isHigh = value > s.maxThreshold;
            newAlerts.push({
              id:          `${s.sensorId}_${now}`,
              machineId:   m.machineId,
              machineName: m.name,
              sensorId:    s.sensorId,
              sensorName:  s.name,
              value,
              threshold:   isHigh ? s.maxThreshold : s.minThreshold,
              direction:   isHigh ? 'high' : 'low',
              severity:    status === 'critical' ? 'CRITICAL' : 'WARNING',
              ts:          now,
              acked:       false,
            });
          }
        });

        // Push to backend every ~20s to avoid flooding
        if (now % 20000 < TICK_MS) {
          const payload = m.sensors.map(s => ({
            sensorId: s.sensorId, type: s.type,
            value: next[s.sensorId]?.currentValue ?? 0, unit: s.unit,
          }));
          telemetryApi.ingest(m.workspaceId, m.machineId, payload).catch(() => {});
        }

        // Update heartbeat
        setHeartbeats(prev => ({
          ...prev,
          [m.machineId]: { machineId: m.machineId, lastSeen: now, status: 'active' },
        }));
      });

      stateRef.current = next;
      setSensorLive(next);

      // Merge new alerts — deduplicate by sensorId (keep latest per sensor, max 100 total)
      if (newAlerts.length > 0) {
        setActiveAlerts(prev => {
          const merged = [...newAlerts, ...prev];
          const seen   = new Set<string>();
          return merged
            .filter(a => { const k = a.sensorId; if (seen.has(k)) return false; seen.add(k); return true; })
            .slice(0, 100);
        });
      }
    }, TICK_MS);

    return () => clearInterval(iv);
  }, [machines]);

  // Heartbeat watchdog — mark machines offline if silent > 30s
  useEffect(() => {
    const iv = setInterval(() => {
      const now = Date.now();
      setHeartbeats(prev => {
        const next = { ...prev };
        Object.values(next).forEach(hb => {
          const machine = machines.find(m => m.machineId === hb.machineId);
          if (machine?.status === 'offline') { next[hb.machineId] = { ...hb, status: 'offline' }; return; }
          const age = now - hb.lastSeen;
          if (age > 30000)      next[hb.machineId] = { ...hb, status: 'offline' };
          else if (age > 10000) next[hb.machineId] = { ...hb, status: 'idle' };
        });
        return next;
      });
    }, 5000);
    return () => clearInterval(iv);
  }, [machines]);

  const ackAlert = useCallback((id: string) => {
    setActiveAlerts(prev => prev.map(a => a.id === id ? { ...a, acked: true } : a));
  }, []);

  const getSensorHistory = useCallback((sensorId: string): TelemetryPoint[] => {
    return stateRef.current[sensorId]?.history ?? [];
  }, []);

  return (
    <TelemetryContext.Provider value={{
      sensorLive, heartbeats, activeAlerts, ackAlert, getSensorHistory,
      isLive: true,
    }}>
      {children}
    </TelemetryContext.Provider>
  );
};

export const useTelemetry = () => useContext(TelemetryContext);
